import React, { useEffect, useState, useCallback } from 'react';
import FloatingCartProduct from '../index/components/Header/floatingcartProduct';
import { useParams, useLocation } from 'react-router-dom';
import Header from '../index/components/Header/Header';
import { TopSection } from './components/TopSection';
import ProductDetailedInfo from './components/MidSection/ProductDetailedInfo';
import FrequentlyBought from '../../shared/components/FrequentlyBought/FrequentlyBought';
import productDetailService from './services/ProductDetail.service';
import RelatedProducts from '@/shared/components/RelatedProducts/RelatedProducts';
import Footer from '@/shared/components/footer/footer';
import MobileFooter from '@/shared/components/MobileFooter/MobileFooter';
import useIsMobile from '@/shared/hooks/useIsMobile';
import { useCart } from '../../contexts/CartContext';
import LoadingSpinner from '../../shared/components/LodingSpiner/LodingSpiner';


interface ProductData {
  _id: string;
  title: string;
  slug: string;
  price: number;
  compareAtPrice?: number;
  description?: string;
  imageUrl?: string;
  images?: Array<{ url: string; altText?: string; isDefault?: boolean }>;
  variants?: any[];
  categories?: Array<{ _id: string; name: string; slug: string }>;
  pricePerMeter?: number;
  quantity?: number;
  hasVariants?: boolean;
  variantHierarchy?: any;
}

const ProductDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  const [product, setProduct] = useState<ProductData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedVariant, setSelectedVariant] = useState<any | null>(null);
  const isMobile = useIsMobile();
  const { addToCart } = useCart();

  const navigationProduct = location.state?.productData as ProductData;

  const fetchProductDetail = useCallback(async (productSlug: string) => {
    try {
      setLoading(true);
      setError(null);

      if (!productSlug) {
        setError('Product slug is required');
        setLoading(false);
        return;
      }
      try {
        const response = await productDetailService.getProductDetail(productSlug);
        if (response?.success && response.data) {
          const productData = Array.isArray(response.data) ? response.data[0] : response.data;
          if (productData) {
            setProduct(productData);
            return;
          }
        }
      } catch (slugError) {
      }
      try {
        const response = await productDetailService.getProductDetail(productSlug);
        if (response?.success && response.data) {
          const productData = Array.isArray(response.data) ? response.data[0] : response.data;
          if (productData) {
            setProduct(productData);
            return;
          }
        }
      } catch (idError) {
      }

      setError('Product not found');
    } catch (err: any) {
      setError('Failed to load product. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (slug) {
      if (navigationProduct) {
        setProduct(navigationProduct);
        setLoading(false);
      }
      fetchProductDetail(slug);
    } else {
      setError('Product slug is required');
      setLoading(false);
    }
  }, [slug, navigationProduct, fetchProductDetail]);
  useEffect(() => {
    if (product && product.variants && product.variants.length > 0) {
      const defaultVariant = product.variants.find((v: any) => v.isDefault) || product.variants[0];
      setSelectedVariant(defaultVariant);
    }
  }, [product]);

  const handleAddToCart = async (variantId: string, quantity: number) => {
    if (!product) return;
    
    // Check if product has variants
    const hasVariants = product.variants && product.variants.length > 0;
    
    // Find the selected variant (only if product has variants)
    const variant = hasVariants 
      ? product.variants?.find((v: any) => v.sku === variantId || v._id === variantId)
      : undefined;
    
    // Use variant image if present and non-empty, else fallback to product image
    let imageUrl = '';
    const variantImage = variant?.variantImages?.[0] || variant?.variantImage;
    if (variant && variantImage && variantImage.trim() !== '') {
      imageUrl = variantImage;
    } else if (variant && variant.imageUrl && variant.imageUrl.trim() !== '') {
      imageUrl = variant.imageUrl;
    } else if (product.imageUrl && product.imageUrl.trim() !== '') {
      imageUrl = product.imageUrl;
    } else if (product.images && product.images.length > 0) {
      imageUrl = product.images[0].url;
    }
    
    // Compose product info for cart
    const productForCart = {
      ...product,
      variantSku: hasVariants ? (variant?.sku || variantId) : '', // Empty string for non-variant products
      variantId: hasVariants ? (variant?._id || variantId) : '', // Empty string for non-variant products
      price: variant?.price || product.price,
      imageUrl,
      name: product.title,
      slug: product.slug,
      description: product.description || '',
    };
    
    // Pass empty string as variantSku for non-variant products, or the variant sku for variant products
    await addToCart(productForCart, quantity, hasVariants ? productForCart.variantSku : '');
  };

  const handleBuyNow = (_variantId: string, _quantity: number) => {
  };

  const handleVariantChange = (variant: any) => {
    setSelectedVariant(variant);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100">
        <header className="bg-white shadow-sm border-b sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Header />
          </div>
        </header>
        <div className="min-h-screen flex items-center justify-center" style={{ padding: '2rem' }}>
          <LoadingSpinner size="large" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100">
        <header className="bg-white shadow-sm border-b sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Header />
          </div>
        </header>
        <div className="min-h-screen flex flex-col items-center justify-center">
          <div className="text-red-500 text-lg mb-4"> {error}</div>
          <button
            onClick={() => slug && fetchProductDetail(slug)}
            className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-lg transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!product) return null;
  const getProductImages = () => {
    // If we have a selected variant with images, use variant images first
    const variantImages = selectedVariant?.variantImages;
    if (selectedVariant && variantImages && variantImages.length > 0) {
      // Combine variant images with product images, variant images first
      const productImages = (product?.images || []).map(img => img.url);
      const allImages = [...variantImages, ...productImages.filter(img => !variantImages.includes(img))];
      return allImages.length > 0 ? allImages : ['/api/placeholder/400/400'];
    }
    
    // Fallback to product images
    if (!product || !product.images || product.images.length === 0) {
      return [product?.imageUrl || '/api/placeholder/400/400'];
    }
    return product.images.map(img => img.url);
  };
  const getMainImage = () => {
    // If we have a selected variant with images, use the first variant image
    const variantImage = selectedVariant?.variantImages?.[0] || selectedVariant?.variantImage;
    if (selectedVariant && variantImage) {
      return variantImage;
    }
    
    // Fallback to product images
    if (!product || !product.images || product.images.length === 0) {
      return product?.imageUrl || '/api/placeholder/400/400';
    }
    const defaultImage = product.images.find(img => img.isDefault);
    if (defaultImage) {
      return defaultImage.url;
    }
    return product.images[0]?.url || '/api/placeholder/400/400';
  };
  const getDisplayTitle = () => {
    return selectedVariant?.variantTitle ? selectedVariant.variantTitle : product.title;
  };
  const convertVariantsForDisplay = () => {
    if (!product) return [];
    
    // If product has no variants, create a synthetic variant for the product itself
    if (!product.variants || product.variants.length === 0) {
      
      return [{
        id: '', // Empty id for non-variant products
        size: product.title,
        price: product.price,
        compareAtPrice: product.compareAtPrice,
        pricePerMeter: product.pricePerMeter || (product.price / 1),
        rollLength: 1,
        attributes: {},
        variantImage: undefined,
      }];
    }
    
    // Handle products with variants
    const mainImage = (product.images && product.images.length > 0) 
      ? (product.images.find(img => img.isDefault)?.url || product.images[0].url) 
      : (product.imageUrl || '/api/placeholder/400/400');
    
    return product.variants.map((variant: any, idx: number) => {
      // Only treat as color variant if it has color/colour attribute, NOT just because it has variantImage
      const isColorVariant = variant.attributes &&
        Object.keys(variant.attributes).some(key =>
          key.toLowerCase().includes('color') || key.toLowerCase().includes('colour')
        );

      let variantImg;
      if (isColorVariant) {
        variantImg = variant.variantImages?.[0] || variant.variantImage;
        if (idx === 0) {
          variantImg = mainImage;
        } else if (!variantImg || variantImg.trim() === '') {
          variantImg = '/api/placeholder/80/80';
        }
      }
      let variantDisplayName = variant.variantTitle || variant.sku;
      if (variant.attributes) {
        const attributeValues = Object.values(variant.attributes);
        if (attributeValues.length > 0) {
          variantDisplayName = attributeValues[0] as string;
        }
      }

      const baseVariant = {
        id: variant.sku || variant._id,
        size: variantDisplayName,
        price: variant.price || product.price,
        compareAtPrice: variant.compareAtPrice,
        pricePerMeter: (variant.price || product.price) / (variant.weight || 1),
        rollLength: variant.weight || 90,
        attributes: variant.attributes || {},
        variantImage: variant.variantImages?.[0] || variant.variantImage || undefined,
        variantImages: variant.variantImages || [],
        ...variant
      };
      if (isColorVariant) {
        baseVariant.variantImage = variantImg;
      }

      return baseVariant;
    });
  };

  return (
    <div className="product-detail">
      <Header />
      <TopSection
        productId={product._id}
        mainImage={getMainImage()}
        thumbnailImages={getProductImages()}
        productName={getDisplayTitle()}
        title={getDisplayTitle()}
        basePrice={selectedVariant?.price || product.price}
        pricePerMeter={product.pricePerMeter || 0}
        variants={convertVariantsForDisplay()}
        hierarchicalVariants={product.hasVariants && product.variants ? product.variants : undefined}
        variantHierarchy={product.variantHierarchy}
        mainProductImage={(product?.images && product.images.length > 0) ? (product.images.find(img => img.isDefault)?.url || product.images[0].url) : (product?.imageUrl || '/api/placeholder/400/400')}
        compareAtPrice={selectedVariant?.compareAtPrice || product.compareAtPrice}
        onAddToCart={handleAddToCart}
        onBuyNow={handleBuyNow}
        onVariantChange={handleVariantChange}
        quantity={selectedVariant?.quantity ?? product.quantity ?? 0}
      />
      <ProductDetailedInfo product={product} selectedVariant={selectedVariant} />
      {product && <FrequentlyBought currentProduct={product} />}
      {product && <RelatedProducts productIdOrSlug={product.slug || product._id} />}
       <FloatingCartProduct/>
      {!isMobile && window.innerWidth > 1200 ? <Footer /> : <MobileFooter />}
    </div>

  );
};

export default ProductDetail;
